import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGroupLeaderPermission } from '@/components/hooks/useGroupLeaderPermission';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { Users, Calendar, AlertCircle, CalendarX, Music } from 'lucide-react';
import { createPageUrl } from '@/utils';
import { getDanceTeamMemberByUser } from '@/lib/teamMemberLookup';

export default function EquipeDanca() {
  const navigate = useNavigate();

  const { user, canManageDance } = useGroupLeaderPermission();

  const { data: danceMember, isLoading: loadingMember } = useQuery({
    queryKey: ['danceMembers', user?.id, user?.email],
    queryFn: () => getDanceTeamMemberByUser(user),
    enabled: !!user
  });

  const hasAccess = !!danceMember || canManageDance;
  const loading = user === undefined || loadingMember;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-gradient-to-br from-orange-600 to-red-700 text-white pt-8 pb-6 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Minist. de Dança MBV" subtitle="Gerenciar ministério de dança" showBack={true} />
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className="bg-white rounded-xl p-8 shadow-sm text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
            <p className="text-gray-600">Apenas integrantes do grupo de dança podem acessar esta área.</p>
          </div>
        </div>
      </div>
    );
  }

  const menuItems = [
    {
      title: 'Hinos',
      description: 'Ver os hinos do louvor',
      icon: Music,
      color: 'bg-purple-500',
      page: 'Hinos'
    },
    {
      title: 'Integrantes',
      description: 'Integrantes do grupo de dança',
      icon: Users,
      color: 'bg-blue-500',
      page: 'IntegrantesDanca'
    },
    {
      title: 'Escalas',
      description: 'Programação do grupo de dança',
      icon: Calendar,
      color: 'bg-green-500',
      page: 'EscalasDanca'
    },
    {
      title: 'Calendário',
      description: 'Bloquear datas indisponíveis',
      icon: CalendarX,
      color: 'bg-orange-500',
      page: 'CalendarioDisponibilidadeDanca'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-orange-600 to-red-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Minist. de Dança MBV" subtitle="Gerenciar ministério de dança" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="mbv-team-menu grid gap-4">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.page}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(createPageUrl(item.page))}
                className="mbv-team-menu-card bg-white rounded-xl p-5 shadow-sm cursor-pointer hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className={`mbv-team-menu-icon w-12 h-12 ${item.color} rounded-xl flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
